import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BuildingOfficeIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  UsersIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  StarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Area,
  AreaChart,
  ScatterChart,
  Scatter,
  Cell
} from 'recharts';

interface FilterProps {
  dateRange: { start: string; end: string; preset: string };
  departments: string[];
  metrics: string[];
  detailLevel: string;
  displayType: string;
  currency: string;
}

interface DepartmentComparisonProps {
  filters: FilterProps;
}

const DepartmentComparison: React.FC<DepartmentComparisonProps> = ({ filters }) => {
  const { t, direction } = useLanguage();
  const [selectedMetric, setSelectedMetric] = useState('budget');
  const [comparisonType, setComparisonType] = useState('performance');

  // Department performance data
  const departmentData = [
    {
      name: 'التسويق الرقمي',
      budget: 850000,
      spent: 624000,
      remaining: 226000,
      utilization: 73.4,
      efficiency: 92,
      roi: 245,
      employees: 12,
      productivity: 87,
      satisfaction: 4.2,
      projects: 8,
      completedProjects: 6,
      revenue: 1530000,
      profit: 906000,
      growth: 18.5,
      performance: 'excellent',
      trend: 'up',
      alerts: 1,
      recommendations: 3
    },
    {
      name: 'المبيعات',
      budget: 720000,
      spent: 648000,
      remaining: 72000,
      utilization: 90.0,
      efficiency: 88,
      roi: 320,
      employees: 18,
      productivity: 91,
      satisfaction: 4.5,
      projects: 12,
      completedProjects: 10,
      revenue: 2073600,
      profit: 1425600,
      growth: 22.3,
      performance: 'excellent',
      trend: 'up',
      alerts: 0,
      recommendations: 2
    },
    {
      name: 'التطوير التقني',
      budget: 950000,
      spent: 741500,
      remaining: 208500,
      utilization: 78.1,
      efficiency: 85,
      roi: 180,
      employees: 22,
      productivity: 82,
      satisfaction: 4.0,
      projects: 6,
      completedProjects: 4,
      revenue: 1334700,
      profit: 593200,
      growth: 12.7,
      performance: 'good',
      trend: 'up',
      alerts: 2,
      recommendations: 4
    },
    {
      name: 'الموارد البشرية',
      budget: 480000,
      spent: 432000,
      remaining: 48000,
      utilization: 90.0,
      efficiency: 78,
      roi: 95,
      employees: 8,
      productivity: 75,
      satisfaction: 3.8,
      projects: 15,
      completedProjects: 12,
      revenue: 410400,
      profit: -21600,
      growth: -2.1,
      performance: 'warning',
      trend: 'down',
      alerts: 3,
      recommendations: 5
    },
    {
      name: 'المالية والمحاسبة',
      budget: 380000,
      spent: 342000,
      remaining: 38000,
      utilization: 90.0,
      efficiency: 95,
      roi: 125,
      employees: 6,
      productivity: 88,
      satisfaction: 4.1,
      projects: 10,
      completedProjects: 9,
      revenue: 427500,
      profit: 85500,
      growth: 8.5,
      performance: 'good',
      trend: 'up',
      alerts: 1,
      recommendations: 2
    },
    {
      name: 'خدمة العملاء',
      budget: 320000,
      spent: 304000,
      remaining: 16000,
      utilization: 95.0,
      efficiency: 82,
      roi: 150,
      employees: 14,
      productivity: 79,
      satisfaction: 4.3,
      projects: 20,
      completedProjects: 18,
      revenue: 456000,
      profit: 152000,
      growth: 15.2,
      performance: 'good',
      trend: 'up',
      alerts: 0,
      recommendations: 3
    }
  ];

  // Performance comparison metrics
  const performanceMetrics = [
    { metric: 'الكفاءة', التسويق: 92, المبيعات: 88, التطوير: 85, الموارد: 78, المالية: 95, العملاء: 82 },
    { metric: 'الإنتاجية', التسويق: 87, المبيعات: 91, التطوير: 82, الموارد: 75, المالية: 88, العملاء: 79 },
    { metric: 'الرضا', التسويق: 84, المبيعات: 90, التطوير: 80, الموارد: 76, المالية: 82, العملاء: 86 },
    { metric: 'الجودة', التسويق: 89, المبيعات: 92, التطوير: 88, الموارد: 81, المالية: 94, العملاء: 87 },
    { metric: 'الابتكار', التسويق: 95, المبيعات: 78, التطوير: 96, الموارد: 65, المالية: 72, العملاء: 74 }
  ];

  // Monthly trend comparison
  const monthlyTrend = [
    { month: 'يناير', التسويق: 68, المبيعات: 85, التطوير: 72, الموارد: 88, المالية: 87, العملاء: 92 },
    { month: 'فبراير', التسويق: 71, المبيعات: 87, التطوير: 74, الموارد: 89, المالية: 88, العملاء: 93 },
    { month: 'مارس', التسويق: 69, المبيعات: 89, التطوير: 76, الموارد: 91, المالية: 89, العملاء: 94 },
    { month: 'أبريل', التسويق: 72, المبيعات: 88, التطوير: 77, الموارد: 89, المالية: 90, العملاء: 95 },
    { month: 'مايو', التسويق: 71, المبيعات: 90, التطوير: 79, الموارد: 90, المالية: 90, العملاء: 95 },
    { month: 'يونيو', التسويق: 73, المبيعات: 90, التطوير: 78, الموارد: 90, المالية: 90, العملاء: 95 }
  ];

  // Department rankings
  const departmentRankings = [
    { department: 'المبيعات', score: 94.2, rank: 1, change: 0, badge: 'ممتاز' },
    { department: 'التسويق الرقمي', score: 91.8, rank: 2, change: 1, badge: 'ممتاز' },
    { department: 'المالية والمحاسبة', score: 88.5, rank: 3, change: 1, badge: 'جيد جداً' },
    { department: 'خدمة العملاء', score: 85.7, rank: 4, change: -1, badge: 'جيد جداً' },
    { department: 'التطوير التقني', score: 82.3, rank: 5, change: 0, badge: 'جيد' },
    { department: 'الموارد البشرية', score: 76.4, rank: 6, change: -2, badge: 'يحتاج تحسين' }
  ];

  const getPerformanceColor = (performance: string) => {
    switch (performance) {
      case 'excellent':
        return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-300';
      case 'good':
        return 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'critical':
        return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const getBadgeColor = (badge: string) => {
    switch (badge) {
      case 'ممتاز':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'جيد جداً':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'جيد':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'يحتاج تحسين':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

  return (
    <div className="space-y-6">
      {/* Department Rankings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <StarIcon className="w-5 h-5 ml-2 rtl:ml-0 rtl:mr-2" />
          ترتيب الأقسام حسب الأداء
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {departmentRankings.map((dept, index) => (
            <motion.div
              key={dept.department}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 hover:shadow-md transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                    dept.rank === 1 ? 'bg-yellow-500' : dept.rank === 2 ? 'bg-gray-400' : dept.rank === 3 ? 'bg-orange-500' : 'bg-gray-300'
                  }`}>
                    {dept.rank}
                  </div>
                  <div className="flex items-center">
                    {dept.change > 0 && <ArrowUpIcon className="w-4 h-4 text-green-500" />}
                    {dept.change < 0 && <ArrowDownIcon className="w-4 h-4 text-red-500" />}
                    {dept.change !== 0 && (
                      <span className={`text-xs ${dept.change > 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {Math.abs(dept.change)}
                      </span>
                    )}
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getBadgeColor(dept.badge)}`}>
                  {dept.badge}
                </span>
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-2">
                {dept.department}
              </h4>
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {dept.score}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  نقطة
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Performance Comparison Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              مقارنة الأداء بين الأقسام
            </h3>
            <select
              value={comparisonType}
              onChange={(e) => setComparisonType(e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="performance">الأداء العام</option>
              <option value="efficiency">الكفاءة</option>
              <option value="productivity">الإنتاجية</option>
              <option value="satisfaction">الرضا</option>
            </select>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={performanceMetrics}>
              <PolarGrid />
              <PolarAngleAxis dataKey="metric" className="text-xs" />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 100]} 
                className="text-xs"
                tick={{ fontSize: 10 }}
              />
              <Radar
                name="التسويق"
                dataKey="التسويق"
                stroke="#3B82F6"
                fill="#3B82F6"
                fillOpacity={0.1}
                strokeWidth={2}
              />
              <Radar
                name="المبيعات"
                dataKey="المبيعات"
                stroke="#10B981"
                fill="#10B981"
                fillOpacity={0.1}
                strokeWidth={2}
              />
              <Radar
                name="التطوير"
                dataKey="التطوير"
                stroke="#F59E0B"
                fill="#F59E0B"
                fillOpacity={0.1}
                strokeWidth={2}
              />
              <Tooltip
                formatter={(value: any, name: any) => [value, name]}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
            </RadarChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            اتجاه الأداء الشهري
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyTrend}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="month" 
                className="text-xs"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                className="text-xs"
                tick={{ fontSize: 12 }}
                domain={[60, 100]}
              />
              <Tooltip
                formatter={(value: any, name: any) => [`${value}%`, name]}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              {colors.map((color, index) => {
                const keys = ['التسويق', 'المبيعات', 'التطوير', 'الموارد', 'المالية', 'العملاء'];
                return (
                  <Line
                    key={keys[index]}
                    type="monotone"
                    dataKey={keys[index]}
                    stroke={color}
                    strokeWidth={2}
                    dot={{ fill: color, strokeWidth: 2, r: 3 }}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Detailed Department Comparison Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            مقارنة تفصيلية للأقسام
          </h3>
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="budget">الميزانية</option>
              <option value="efficiency">الكفاءة</option>
              <option value="roi">العائد على الاستثمار</option>
              <option value="productivity">الإنتاجية</option>
            </select>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">القسم</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">الميزانية</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">المُنفق</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">الاستخدام</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">الكفاءة</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">العائد</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">الموظفين</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">الأداء</th>
              </tr>
            </thead>
            <tbody>
              {departmentData.map((dept, index) => (
                <motion.tr
                  key={dept.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-3 rtl:space-x-reverse">
                      <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{dept.name}</span>
                        <div className="flex items-center space-x-1 rtl:space-x-reverse mt-1">
                          {dept.alerts > 0 && (
                            <div className="flex items-center">
                              <ExclamationTriangleIcon className="w-3 h-3 text-red-500" />
                              <span className="text-xs text-red-500 ml-1">{dept.alerts}</span>
                            </div>
                          )}
                          <span className="text-xs text-blue-500">{dept.recommendations} توصية</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-900 dark:text-white font-medium">
                    {(dept.budget / 1000).toLocaleString()} ألف ر.س
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                    {(dept.spent / 1000).toLocaleString()} ألف ر.س
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {dept.utilization.toFixed(1)}%
                      </span>
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 max-w-16">
                        <div 
                          className={`h-2 rounded-full transition-all duration-1000 ${
                            dept.utilization > 85 ? 'bg-red-500' : dept.utilization > 70 ? 'bg-yellow-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(dept.utilization, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {dept.efficiency}%
                      </span>
                      {dept.trend === 'up' ? (
                        <ArrowTrendingUpIcon className="w-4 h-4 text-green-500" />
                      ) : (
                        <ArrowTrendingDownIcon className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-green-600 dark:text-green-400">
                    {dept.roi}%
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                    {dept.employees} موظف
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPerformanceColor(dept.performance)}`}>
                      {dept.performance === 'excellent' ? 'ممتاز' : dept.performance === 'good' ? 'جيد' : 'تحذير'}
                    </span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Budget vs Spending Comparison */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          مقارنة الميزانية والإنفاق
        </h3>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={departmentData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis 
              dataKey="name" 
              className="text-xs"
              tick={{ fontSize: 10 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              className="text-xs"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => `${(value / 1000).toLocaleString()}ك`}
            />
            <Tooltip
              formatter={(value: any, name: any) => [
                `${(value / 1000).toLocaleString()} ألف ر.س`, 
                name === 'budget' ? 'الميزانية' : name === 'spent' ? 'المُنفق' : 'المتبقي'
              ]}
              labelStyle={{ color: '#374151' }}
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}
            />
            <Bar dataKey="budget" fill="#3B82F6" name="budget" radius={[4, 4, 0, 0]} />
            <Bar dataKey="spent" fill="#10B981" name="spent" radius={[4, 4, 0, 0]} />
            <Bar dataKey="remaining" fill="#F59E0B" name="remaining" radius={[4, 4, 0, 0]} />
          </ComposedChart>
        </ResponsiveContainer>
      </motion.div>
    </div>
  );
};

export default DepartmentComparison;
